import { Injectable, NgZone } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { EstablishmentService } from 'app/modules/establishment/establishment.service';
import { environment } from 'environments/environment';
import {
    AnalyticsExclusion,
    BUFFER_KEY,
    OUTBOX_KEY,
    SESSION_KEY_PREFIX,
    newSessionId,
    readAnalyticsExclusion,
    readJson,
    safeSessionRemove,
    writeJson,
} from './menu-analytics.session';

// -----------------------------------------------------------------------------------------------------
// @ Tuning
// -----------------------------------------------------------------------------------------------------

/** Flush once this many gestures are buffered. */
const FLUSH_AT_EVENTS = 20;

/** Flush after this much inactivity, the timer being armed by the first buffered event. */
const IDLE_FLUSH_MS = 10_000;

/**
 * Longest wait for an idle moment before the session start runs anyway. Only reached on a page
 * kept busy that long; the work it guards is a few storage accesses and no network.
 */
const SESSION_START_IDLE_TIMEOUT_MS = 2_000;

/** Delay used where `requestIdleCallback` does not exist, iOS Safari first among them. */
const SESSION_START_FALLBACK_MS = 1_000;

/**
 * A section chip is not counted again before this delay. Dishes are deliberately NOT debounced:
 * two openings of the same card are two legitimate consultations.
 */
const CATEGORY_DEBOUNCE_MS = 30_000;

/** A session dies after this much inactivity. */
const SESSION_IDLE_MS = 30 * 60_000;

/** Absolute session ceiling, matching the server-side clamp. */
const SESSION_MAX_MS = 6 * 60 * 60_000;

/**
 * Batches sent per session. The server excludes the WHOLE session past 20 batches instead of
 * clamping, so the client stops well short of it and simply goes quiet.
 */
const MAX_BATCHES = 16;

/** Entries (dishes plus sections) per batch. The server ceiling is 50. */
const MAX_ENTRIES_PER_BATCH = 40;

/** Value of a single counter inside one batch. The server ceiling is 200. */
const MAX_COUNTER = 150;

/** Body size handed to the network. The server ceiling is 32 768 characters. */
const MAX_BODY_CHARS = 24_000;

/** Unconfirmed batches kept in the mirror. Oldest are dropped first. */
const MAX_OUTBOX_BATCHES = 8;

/** Unconfirmed batches older than this are dropped without a trace. */
const OUTBOX_TTL_MS = 12 * 60 * 60_000;

/** Bound on the per-session "already counted" sets mirrored in `sessionStorage`. */
const MAX_SEEN = 300;

// -----------------------------------------------------------------------------------------------------
// @ Shapes
// -----------------------------------------------------------------------------------------------------

/** One dish, with the counters accumulated for it since the previous batch. */
interface ItemEntry {
    uuid: string;
    categoryUuid?: string;
    opens: number;
    firstInSession: boolean;
}

/** One section, with the counters accumulated for it since the previous batch. */
interface CategoryEntry {
    uuid: string;
    taps: number;
    firstInSession: boolean;
}

/**
 * State of the current session, mirrored in `sessionStorage` under a key scoped to the
 * establishment. It dies with the tab.
 */
interface SessionState {
    /** Random identifier, never derived from a device signal. */
    id: string;
    /** Epoch ms of the session start. Only relative durations ever leave the browser. */
    startedAt: number;
    /** Epoch ms of the last activity, a gesture or a menu page load, for the 30-minute expiry. */
    lastAt: number;
    /** Highest sequence number assigned so far. */
    seq: number;
    /** Batches enqueued so far, capped well under the server ceiling. */
    batches: number;
    /**
     * Milliseconds between the session start, anchored on the page load, and its first event: the
     * start buffered once the menu is displayed, or an earlier gesture.
     */
    firstEventAtMs: number | null;
    /**
     * First display language of the session, sent as `menuLanguage`. Kept for compatibility: the
     * session is counted in `cur`.
     */
    lang: string | null;
    /**
     * Language the visitor last chose, sent as `currentLanguage`: the session is counted in it,
     * once, so percentages still total 100. Null until the first switch, meaning `lang`; absent
     * from a session stored by an earlier version of this service.
     */
    cur?: string | null;
    /** Whether the visitor switched language at any point. Counted once. */
    changed: boolean;
    /** Dishes already counted in this session. The client owns this state, no server row does. */
    items: string[];
    /** Sections already counted in this session. */
    cats: string[];
}

/** One batch handed to the network, kept until the server confirms it. */
interface PendingBatch {
    /** Establishment uuid, which is the endpoint path. */
    e: string;
    /** Epoch ms of the enqueue, for the 12-hour purge. */
    at: number;
    /** Epoch ms of the session start, so `sessionAgeMs` can be recomputed at send time. */
    s0: number;
    /** Payload, minus `sessionAgeMs` which is only known at send time. */
    body: Record<string, unknown>;
}

/** Mirror of the live buffer, for gestures not yet turned into a batch. */
interface BufferMirror {
    e: string;
    m?: string;
    at: number;
    n: number;
    items: ItemEntry[];
    cats: CategoryEntry[];
}

// -----------------------------------------------------------------------------------------------------
// @ Service
// -----------------------------------------------------------------------------------------------------

/**
 * Menu interaction counters for the public customer menu.
 *
 * <h2>What it measures</h2>
 * The display of a menu page opens the session and records its display language and the browser
 * language, so a visitor who only reads or scrolls is counted in the hourly traffic and in the
 * language breakdown. A manual switch moves the visit to the newly chosen language, the one the
 * menu is actually read in. A dish consultation is the opening of its detail sheet, and a section
 * consultation is a tap on its navigation chip. Scroll-based section detection is deliberately NOT
 * instrumented: it fires continuously, expresses no intent, and the first section is selected
 * without any user action at load, which would put it in a hundred per cent of sessions.
 *
 * <h2>Why it does not use HttpClient</h2>
 * Two concrete regressions are avoided. `NoIndexInterceptor` adds a header to every request issued
 * from a menu page, which would make each flush preflighted. And `errorInterceptor` reads a nested
 * field with no null guard on any error coming from the API: an error from this endpoint would
 * throw, and an error with the matching status would redirect the end customer to the not-found
 * page. The transport is therefore a bare `fetch` with `keepalive`, falling back to
 * `navigator.sendBeacon`, with a `text/plain` content type so the request stays CORS-simple and
 * triggers no preflight, and without credentials so the year-long fingerprint cookie never travels.
 *
 * <h2>What it costs the menu</h2>
 * Nothing on the display path. The service is never instantiated during `APP_INITIALIZER`. It is
 * armed at the first idle moment after a menu page has been displayed, or on the visitor's first
 * gesture if that comes first; every piece of work runs outside the Angular zone and no component
 * state is ever touched. Opening the session sends nothing by itself: its start leaves with the
 * usual flush triggers, never before ten seconds unless the tab is hidden or closed first.
 *
 * <h2>What it explicitly does not use</h2>
 * The `qrsto_fp` browser fingerprint and the `X-Fingerprint` header. That value is a tracker
 * subject to consent, its current purpose is the contest anti-fraud check, and its cookie is set
 * at the domain root and is therefore shared by every restaurant. It must not be reintroduced here.
 */
@Injectable({ providedIn: 'root' })
export class MenuAnalyticsService {

    /** Listeners installed, storage read: done once, at the session start or on an earlier gesture. */
    private _armed = false;

    /** The session start has been scheduled for this page load. */
    private _startScheduled = false;

    /** Set when the session is excluded or has spent its batch budget. Nothing is emitted after. */
    private _stopped = false;

    /** The `skip` parameter, carried on every batch. */
    private _excluded = false;

    /** The preview token, carried on every batch. Its presence alone excludes the session. */
    private _token: string | undefined;

    private _establishmentUuid: string | null = null;
    private _menuUuid: string | null = null;

    private _session: SessionState | null = null;

    private _pendingItems = new Map<string, ItemEntry>();
    private _pendingCats = new Map<string, CategoryEntry>();
    private _buffered = 0;
    private _dirty = false;

    /** Last tap per section, for the 30-second debounce. In memory only, on purpose. */
    private _catAt: Record<string, number> = {};

    private _outbox: PendingBatch[] = [];
    private _outboxLoaded = false;
    private _draining = false;

    private _idleTimer: any = null;
    private _replayScheduled = false;

    /**
     * Constructor. Does strictly nothing: no storage access, no listener, no timer. The service is
     * a root singleton, so its construction must stay free wherever it happens to be injected.
     */
    constructor(
        private _zone: NgZone,
        private _translocoService: TranslocoService,
        private _establishmentService: EstablishmentService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public API
    // -----------------------------------------------------------------------------------------------------

    /**
     * Declares which menu the visitor is looking at, without arming anything.
     *
     * <p>Called from the menu page so that a section tap arriving before any other gesture still
     * carries its menu. One batch carries one menu, so a change flushes what belonged to the
     * previous one.
     */
    setMenuContext(menuUuid: string | null | undefined): void {
        if (!menuUuid || menuUuid === this._menuUuid) {
            return;
        }
        if (!this._armed) {
            // Nothing has been recorded yet: remember the context and stay dormant.
            this._menuUuid = menuUuid;
            return;
        }
        this._zone.runOutsideAngular(() => {
            try {
                this._applyMenuContext(menuUuid);
            } catch {
                // Analytics never surface an error on the public menu.
            }
        });
    }

    /**
     * Counts the opening of a dish detail sheet.
     *
     * <p>Two openings of the same dish in one session are two consultations and are NOT
     * deduplicated. Only `firstInSession` is deduplicated, and the client is the sole holder of
     * that state: no server row ever links a session to a dish.
     */
    trackItemOpen(itemUuid: string, categoryUuid?: string, menuUuid?: string): void {
        if (!itemUuid) {
            return;
        }
        this._zone.runOutsideAngular(() => {
            try {
                if (!this._ensureArmed()) {
                    return;
                }
                this._applyMenuContext(menuUuid);
                const session = this._touchSession();
                if (!session) {
                    return;
                }
                const existing = this._pendingItems.get(itemUuid);
                if (existing) {
                    existing.opens = Math.min(existing.opens + 1, MAX_COUNTER);
                    if (categoryUuid && !existing.categoryUuid) {
                        existing.categoryUuid = categoryUuid;
                    }
                } else {
                    this._pendingItems.set(itemUuid, {
                        uuid: itemUuid,
                        categoryUuid: categoryUuid || undefined,
                        opens: 1,
                        firstInSession: this._markSeen(session.items, itemUuid),
                    });
                }
                this._afterEvent();
            } catch {
                // Never visible, never blocking.
            }
        });
    }

    /**
     * Counts a tap on a section navigation chip.
     *
     * <p>Debounced over 30 seconds: a visitor bouncing between two chips is one intent, not ten.
     */
    trackCategoryTap(categoryUuid: string, menuUuid?: string): void {
        if (!categoryUuid) {
            return;
        }
        this._zone.runOutsideAngular(() => {
            try {
                if (!this._ensureArmed()) {
                    return;
                }
                const now = Date.now();
                const last = this._catAt[categoryUuid];
                if (last && now - last < CATEGORY_DEBOUNCE_MS) {
                    return;
                }
                this._catAt[categoryUuid] = now;

                this._applyMenuContext(menuUuid);
                const session = this._touchSession();
                if (!session) {
                    return;
                }
                const existing = this._pendingCats.get(categoryUuid);
                if (existing) {
                    existing.taps = Math.min(existing.taps + 1, MAX_COUNTER);
                } else {
                    this._pendingCats.set(categoryUuid, {
                        uuid: categoryUuid,
                        taps: 1,
                        firstInSession: this._markSeen(session.cats, categoryUuid),
                    });
                }
                this._afterEvent();
            } catch {
                // Never visible, never blocking.
            }
        });
    }

    /**
     * Records a manual language switch.
     *
     * <p>The session is counted in the last language the visitor chose, the one they actually read
     * the menu in, so the new language is remembered as the current one and leaves with every
     * batch: several switches in a row end on the last one, and a switch made before the first
     * batch counts straight away in the new language.
     *
     * <p>Must be called BEFORE the switch is applied: a session opened by this very gesture starts
     * in the previous language, which stays its first one. `languageChanged` is a measure in
     * itself — it says the automatic detection is getting it wrong.
     */
    trackLanguageChange(previousLang: string, nextLang: string): void {
        this._zone.runOutsideAngular(() => {
            try {
                if (!this._ensureArmed()) {
                    return;
                }
                const session = this._touchSession();
                if (!session) {
                    return;
                }
                if (!session.lang && previousLang) {
                    session.lang = previousLang;
                }
                if (nextLang && previousLang && nextLang !== previousLang) {
                    session.changed = true;
                }
                if (nextLang) {
                    session.cur = nextLang;
                }
                this._afterEvent();
            } catch {
                // Never visible, never blocking.
            }
        });
    }

    /**
     * Records the opening of a menu.
     *
     * <p>Only ever reached in multi-menu, and in single-menu when the menu is a PDF — both are a
     * deliberate navigation. A single digital menu is rendered inline with no click, so nothing is
     * counted there, which is correct: there is no opening to count.
     */
    trackMenuOpen(menuUuid: string): void {
        if (!menuUuid) {
            return;
        }
        this._zone.runOutsideAngular(() => {
            try {
                if (!this._ensureArmed()) {
                    return;
                }
                this._applyMenuContext(menuUuid);
                if (!this._touchSession()) {
                    return;
                }
                this._afterEvent();
            } catch {
                // Never visible, never blocking.
            }
        });
    }

    /**
     * Replays batches left unconfirmed by a previous page load.
     *
     * <p>Deferred to browser idle time so it never competes with the first paint, and a no-op when
     * nothing is pending — which is the overwhelmingly common case.
     */
    replayPending(): void {
        if (this._replayScheduled || this._stopped) {
            return;
        }
        this._replayScheduled = true;
        this._zone.runOutsideAngular(() => {
            const run = () => {
                try {
                    if (this._stopped || this._isExcluded()) {
                        return;
                    }
                    this._loadOutbox();
                    if (this._outbox.length) {
                        this._resolveEstablishment();
                        this._drain(false);
                    }
                } catch {
                    // Never visible, never blocking.
                }
            };
            const idle = (window as any).requestIdleCallback;
            if (typeof idle === 'function') {
                idle(run, { timeout: 8000 });
            } else {
                setTimeout(run, 5000);
            }
        });
    }

    /**
     * Opens the measurement session once a menu page is on screen, so that a visitor who only reads
     * or scrolls is counted too: in the hourly traffic, and by display and browser language.
     *
     * <p>Called by every page that shows a menu: the establishment page, whatever it renders (a
     * single digital menu, a single PDF menu or the list of menus), and the menu page, reached from
     * that list or by a direct link. Only the first call of a page load schedules anything.
     *
     * <p>One start per session. It is buffered only when the tab has no live session: a reload finds
     * the session in `sessionStorage` and emits nothing new, an internal navigation reuses the one in
     * memory, and a new tab starts with an empty `sessionStorage`, hence a new session. Gestures keep
     * feeding that same session.
     *
     * <p>Never on the display path: never called from `APP_INITIALIZER`, deferred to the first idle
     * moment after the first paint, run outside the Angular zone. It sends nothing by itself; the
     * batch leaves with the usual triggers — ten seconds of inactivity, tab hidden or closed, back
     * online. Silent, like every other method, when `skip` or `previewToken` is active or when the
     * browser is under automation control.
     */
    trackSessionStart(): void {
        if (this._startScheduled || this._stopped) {
            return;
        }
        this._startScheduled = true;
        this._zone.runOutsideAngular(() => {
            try {
                this._whenIdle(() => this._startWhenDisplayed());
            } catch {
                // Never visible, never blocking.
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Arming
    // -----------------------------------------------------------------------------------------------------

    /**
     * Installs listeners and reads storage, once: at the session start that follows the display of
     * a menu page, or on an earlier gesture.
     *
     * @returns false when the session must stay silent
     */
    private _ensureArmed(): boolean {
        if (this._stopped) {
            return false;
        }
        if (this._armed) {
            return true;
        }
        this._armed = true;

        if (this._isExcluded()) {
            // The restaurateur's own browsing, or a draft preview. Strictly nothing is emitted.
            this._stopped = true;
            return false;
        }

        this._resolveEstablishment();
        this._loadSession();
        this._loadOutbox();
        this._loadBuffer();

        document.addEventListener('visibilitychange', this._onVisibility);
        window.addEventListener('pagehide', this._onPageHide);
        window.addEventListener('online', this._onOnline);

        return true;
    }

    /**
     * Reads the exclusion flags captured at boot, and caches them for the batch payload.
     *
     * <p>A browser under automation control is silenced the same way, see {@link _isAutomated}.
     */
    private _isExcluded(): boolean {
        const flags: AnalyticsExclusion | null = readAnalyticsExclusion();
        this._excluded = !!(flags && flags.excluded);
        this._token = flags && flags.token ? flags.token : undefined;
        return this._excluded || !!this._token || this._isAutomated();
    }

    /**
     * Whether the browser is under automation control, as `navigator.webdriver` reports for
     * WebDriver-based tools and, by default, for Playwright and Puppeteer.
     *
     * <p>The session now opens when the menu is displayed rather than on a human gesture, so a test
     * runner or a scripted browser would otherwise be counted as a visitor. Such a browser emits
     * nothing at all, gestures included. The server-side user-agent list stays the second belt.
     */
    private _isAutomated(): boolean {
        try {
            return typeof navigator !== 'undefined' && navigator.webdriver === true;
        } catch {
            return false;
        }
    }

    /**
     * Picks up the establishment uuid. The establishment is already resolved by the time any
     * gesture can happen, so the replay subject answers synchronously.
     */
    private _resolveEstablishment(): void {
        if (this._establishmentUuid) {
            return;
        }
        try {
            this._establishmentService.establishment$.subscribe((establishment) => {
                if (establishment && establishment.uuid) {
                    this._establishmentUuid = establishment.uuid;
                }
            });
        } catch {
            // Without a uuid nothing can be sent; the buffer simply never leaves the browser.
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Session start
    // -----------------------------------------------------------------------------------------------------

    /**
     * Runs the given work at the first idle moment, which comes after the first paint.
     * `requestIdleCallback` does not exist on iOS Safari, the dominant browser on a QR menu, hence
     * the timer.
     */
    private _whenIdle(run: () => void): void {
        try {
            const idle = (window as any).requestIdleCallback;
            if (typeof idle === 'function') {
                idle(run, { timeout: SESSION_START_IDLE_TIMEOUT_MS });
                return;
            }
        } catch {
            // Fall back to the timer.
        }
        setTimeout(run, SESSION_START_FALLBACK_MS);
    }

    /**
     * Starts the session now if the page is on screen, otherwise on its first display.
     *
     * <p>A tab opened in the background, or a prerendered page, has displayed nothing yet: nobody has
     * seen the menu, so nothing is counted until someone actually looks at it.
     */
    private _startWhenDisplayed(): void {
        try {
            if (!this._isPageHidden()) {
                this._openSessionOnLoad();
                return;
            }
            const onShow = (): void => {
                if (this._isPageHidden()) {
                    return;
                }
                document.removeEventListener('visibilitychange', onShow);
                this._openSessionOnLoad();
            };
            document.addEventListener('visibilitychange', onShow);
        } catch {
            // Never visible, never blocking.
        }
    }

    /**
     * Buffers the start of the session: a batch with no dish and no section, carrying only the
     * session itself, its first display language and the browser language.
     *
     * <p>Nothing is buffered when the tab already has a live session — restored from
     * `sessionStorage` after a reload, an expired one never being restored, or opened by a gesture
     * that beat the idle callback. That session has already emitted its start, or carries it in the
     * batch being buffered, so the page load only counts as activity: a visitor who reloads and goes
     * on reading is not split in two by the inactivity expiry.
     */
    private _openSessionOnLoad(): void {
        try {
            if (!this._ensureArmed()) {
                return;
            }
            const now = Date.now();
            const live = this._session;
            if (live && now - live.lastAt <= SESSION_IDLE_MS && now - live.startedAt <= SESSION_MAX_MS) {
                live.lastAt = now;
                if (live.seq === 0 && !this._dirty) {
                    // Started, but the previous page died before handing its first batch to the
                    // network: this start is still the only one the server will ever see.
                    this._afterEvent();
                    return;
                }
                this._persistSession();
                if (this._dirty) {
                    // Whatever is buffered leaves with the usual idle flush, including gestures
                    // restored from the buffer mirror, which no timer covered yet.
                    this._restartIdleTimer();
                }
                return;
            }
            // No live session in this tab: open one, rotating an expired one, and buffer its start.
            if (!this._touchSession()) {
                return;
            }
            this._afterEvent();
        } catch {
            // Never visible, never blocking.
        }
    }

    private _isPageHidden(): boolean {
        try {
            return document.visibilityState === 'hidden';
        } catch {
            return false;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Session
    // -----------------------------------------------------------------------------------------------------

    /**
     * Returns the live session, rotating it when it has expired.
     */
    private _touchSession(): SessionState | null {
        const now = Date.now();
        let session = this._session;

        if (session && (now - session.lastAt > SESSION_IDLE_MS || now - session.startedAt > SESSION_MAX_MS)) {
            // A reload in the same tab keeps the session; a three-hour gap is a second visit.
            this._enqueueBuffer();
            session = null;
        }
        if (!session) {
            session = this._newSession(now);
            this._catAt = {};
        }

        session.lastAt = now;
        if (session.firstEventAtMs === null) {
            session.firstEventAtMs = Math.max(0, Math.min(now - session.startedAt, SESSION_MAX_MS));
        }
        if (!session.lang) {
            session.lang = this._activeLang();
        }

        this._session = session;
        return session;
    }

    /**
     * Draws a new session. The start is anchored on the page load when the browser exposes it, so
     * `firstEventAtMs` carries the real delay before the first event.
     */
    private _newSession(now: number): SessionState {
        let startedAt = now;
        try {
            const origin = typeof performance !== 'undefined' ? Math.round(performance.timeOrigin) : 0;
            if (origin > 0 && now - origin >= 0 && now - origin < SESSION_MAX_MS) {
                startedAt = origin;
            }
        } catch {
            // Keep `now`.
        }
        return {
            id: newSessionId(),
            startedAt,
            lastAt: now,
            seq: 0,
            batches: 0,
            firstEventAtMs: null,
            lang: null,
            cur: null,
            changed: false,
            items: [],
            cats: [],
        };
    }

    private _loadSession(): void {
        if (!this._establishmentUuid) {
            return;
        }
        const stored = readJson<SessionState>(SESSION_KEY_PREFIX + this._establishmentUuid);
        if (!stored || !stored.id || typeof stored.startedAt !== 'number') {
            return;
        }
        const now = Date.now();
        if (now - stored.lastAt > SESSION_IDLE_MS || now - stored.startedAt > SESSION_MAX_MS) {
            return;
        }
        stored.items = Array.isArray(stored.items) ? stored.items : [];
        stored.cats = Array.isArray(stored.cats) ? stored.cats : [];
        this._session = stored;
    }

    private _persistSession(): void {
        if (!this._session || !this._establishmentUuid) {
            return;
        }
        writeJson(SESSION_KEY_PREFIX + this._establishmentUuid, this._session);
    }

    /**
     * Marks a uuid as counted for this session and says whether it is the first time.
     *
     * <p>The set is bounded: past the ceiling everything reads as already counted, which
     * under-reports distinct sessions rather than letting the mirror grow without limit.
     */
    private _markSeen(seen: string[], uuid: string): boolean {
        if (seen.indexOf(uuid) !== -1 || seen.length >= MAX_SEEN) {
            return false;
        }
        seen.push(uuid);
        return true;
    }

    private _activeLang(): string | null {
        try {
            return this._translocoService.getActiveLang() || null;
        } catch {
            return null;
        }
    }

    private _browserLang(): string | undefined {
        try {
            return (navigator.language || '').slice(0, 35) || undefined;
        } catch {
            return undefined;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Buffer
    // -----------------------------------------------------------------------------------------------------

    /**
     * Common tail of every gesture: mirror, then decide whether to flush now or arm the idle timer.
     */
    private _afterEvent(): void {
        this._buffered++;
        this._dirty = true;
        this._persistSession();
        this._persistBuffer();

        if (this._buffered >= FLUSH_AT_EVENTS || this._entryCount() >= MAX_ENTRIES_PER_BATCH) {
            this._flush(false);
            return;
        }
        this._restartIdleTimer();
    }

    private _entryCount(): number {
        return this._pendingItems.size + this._pendingCats.size;
    }

    private _resetBuffer(): void {
        this._pendingItems.clear();
        this._pendingCats.clear();
        this._buffered = 0;
        this._dirty = false;
        safeSessionRemove(BUFFER_KEY);
    }

    private _persistBuffer(): void {
        if (!this._establishmentUuid) {
            return;
        }
        writeJson(BUFFER_KEY, {
            e: this._establishmentUuid,
            m: this._menuUuid || undefined,
            at: Date.now(),
            n: this._buffered,
            items: Array.from(this._pendingItems.values()),
            cats: Array.from(this._pendingCats.values()),
        } as BufferMirror);
    }

    /**
     * Restores gestures that a crash or a kill left in the live buffer.
     */
    private _loadBuffer(): void {
        const stored = readJson<BufferMirror>(BUFFER_KEY);
        if (!stored) {
            return;
        }
        if (stored.e !== this._establishmentUuid || Date.now() - stored.at > OUTBOX_TTL_MS) {
            safeSessionRemove(BUFFER_KEY);
            return;
        }
        (stored.items || []).slice(0, MAX_ENTRIES_PER_BATCH).forEach((item) => {
            if (item && item.uuid) {
                this._pendingItems.set(item.uuid, item);
            }
        });
        (stored.cats || []).slice(0, MAX_ENTRIES_PER_BATCH).forEach((cat) => {
            if (cat && cat.uuid) {
                this._pendingCats.set(cat.uuid, cat);
            }
        });
        if (this._entryCount()) {
            this._buffered = Math.max(1, Math.min(stored.n || 0, FLUSH_AT_EVENTS - 1));
            this._dirty = true;
            if (!this._menuUuid && stored.m) {
                this._menuUuid = stored.m;
            }
        }
    }

    /**
     * One batch carries one menu, so a change of menu flushes what belonged to the previous one.
     */
    private _applyMenuContext(menuUuid?: string | null): void {
        if (!menuUuid || menuUuid === this._menuUuid) {
            return;
        }
        if (this._dirty) {
            this._flush(false);
        }
        this._menuUuid = menuUuid;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Triggers
    // -----------------------------------------------------------------------------------------------------

    /**
     * The only trigger that is reliable on mobile: close events are not guaranteed on iOS.
     * Timers are stopped while the tab is hidden.
     */
    private _onVisibility = (): void => {
        try {
            if (document.visibilityState === 'hidden') {
                this._clearIdleTimer();
                this._flush(true);
            } else if (this._dirty) {
                // Coming back with something still buffered: the idle timer was stopped while
                // hidden, so it has to be armed again or that batch would wait for the next
                // gesture.
                this._restartIdleTimer();
            }
        } catch {
            // Never visible, never blocking.
        }
    };

    private _onPageHide = (): void => {
        try {
            this._clearIdleTimer();
            this._flush(true);
        } catch {
            // Never visible, never blocking.
        }
    };

    private _onOnline = (): void => {
        try {
            this._drain(false);
        } catch {
            // Never visible, never blocking.
        }
    };

    private _clearIdleTimer(): void {
        if (this._idleTimer) {
            clearTimeout(this._idleTimer);
            this._idleTimer = null;
        }
    }

    private _restartIdleTimer(): void {
        this._clearIdleTimer();
        try {
            if (document.visibilityState === 'hidden') {
                return;
            }
        } catch {
            // Assume visible.
        }
        this._idleTimer = setTimeout(() => {
            this._idleTimer = null;
            try {
                this._flush(false);
            } catch {
                // Never visible, never blocking.
            }
        }, IDLE_FLUSH_MS);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Batching
    // -----------------------------------------------------------------------------------------------------

    private _flush(unloading: boolean): void {
        this._clearIdleTimer();
        this._enqueueBuffer();
        this._drain(unloading);
    }

    /**
     * Turns the live buffer into one batch, appended to the outbox. The sequence is assigned here
     * and the outbox is drained strictly in order, so a batch left behind by a failure is never
     * overtaken and rendered inert by a later one.
     */
    private _enqueueBuffer(): void {
        if (!this._dirty) {
            return;
        }
        const session = this._session;
        if (!session) {
            this._resetBuffer();
            return;
        }
        if (!this._establishmentUuid) {
            this._resolveEstablishment();
            if (!this._establishmentUuid) {
                // Keep buffering rather than losing the gestures.
                return;
            }
        }
        if (session.batches >= MAX_BATCHES) {
            // Past 20 batches the server excludes the whole session. Going quiet is the safe end.
            this._resetBuffer();
            this._stopped = true;
            return;
        }

        // Ordered so concurrent batches touch the same rows in the same order server-side.
        const items = Array.from(this._pendingItems.values())
            .sort(this._byUuid)
            .slice(0, MAX_ENTRIES_PER_BATCH);
        const categories = Array.from(this._pendingCats.values())
            .sort(this._byUuid)
            .slice(0, Math.max(0, MAX_ENTRIES_PER_BATCH - items.length));

        session.seq++;
        session.batches++;

        const body: Record<string, unknown> = {
            sessionId: session.id,
            sequence: session.seq,
            firstEventAtMs: session.firstEventAtMs || 0,
            menuLanguage: session.lang || undefined,
            // Read from the session and not from Transloco: a switch can flush the buffer before
            // it is applied, and the batch must already carry the new language.
            currentLanguage: session.cur || session.lang || undefined,
            browserLanguage: this._browserLang(),
            languageChanged: !!session.changed,
            menuUuid: this._menuUuid || undefined,
            excluded: this._excluded,
            previewToken: this._token,
            items,
            categories,
        };

        this._outbox.push({ e: this._establishmentUuid, at: Date.now(), s0: session.startedAt, body });
        this._trimOutbox();
        this._resetBuffer();
        this._persistSession();
        this._persistOutbox();
    }

    private _byUuid = (a: { uuid: string }, b: { uuid: string }): number =>
        a.uuid < b.uuid ? -1 : a.uuid > b.uuid ? 1 : 0;

    // -----------------------------------------------------------------------------------------------------
    // @ Outbox
    // -----------------------------------------------------------------------------------------------------

    private _loadOutbox(): void {
        if (this._outboxLoaded) {
            return;
        }
        this._outboxLoaded = true;
        const stored = readJson<PendingBatch[]>(OUTBOX_KEY);
        if (Array.isArray(stored)) {
            this._outbox = stored.filter((entry) => entry && entry.e && entry.body);
            this._purgeOutbox();
        }
    }

    private _persistOutbox(): void {
        if (!this._outbox.length) {
            safeSessionRemove(OUTBOX_KEY);
            return;
        }
        writeJson(OUTBOX_KEY, this._outbox);
    }

    /** Drops what a twelve-hour-old offline stretch left behind. */
    private _purgeOutbox(): void {
        const now = Date.now();
        const kept = this._outbox.filter((entry) => now - (entry.at || 0) < OUTBOX_TTL_MS);
        if (kept.length !== this._outbox.length) {
            this._outbox = kept;
            this._persistOutbox();
        }
    }

    /** Keeps the mirror bounded in number and in size, dropping the oldest entries first. */
    private _trimOutbox(): void {
        if (this._outbox.length > MAX_OUTBOX_BATCHES) {
            this._outbox = this._outbox.slice(this._outbox.length - MAX_OUTBOX_BATCHES);
        }
        let guard = 0;
        while (this._outbox.length > 1 && guard++ < MAX_OUTBOX_BATCHES) {
            let size = 0;
            try {
                size = JSON.stringify(this._outbox).length;
            } catch {
                size = 0;
            }
            if (size <= MAX_BODY_CHARS * MAX_OUTBOX_BATCHES) {
                return;
            }
            this._outbox.shift();
        }
    }

    /**
     * Sends the outbox head, and only removes it once the server has answered. Strictly in order:
     * a failure stops the drain and leaves everything for the next trigger or the `online` event.
     */
    private _drain(unloading: boolean): void {
        if (this._draining) {
            return;
        }
        this._loadOutbox();
        this._purgeOutbox();
        if (!this._outbox.length) {
            return;
        }

        const entry = this._outbox[0];
        const payload = {
            ...entry.body,
            sessionAgeMs: Math.max(0, Math.min(Date.now() - (entry.s0 || Date.now()), SESSION_MAX_MS)),
        };

        let text: string;
        try {
            text = JSON.stringify(payload);
        } catch {
            this._outbox.shift();
            this._persistOutbox();
            return;
        }
        if (text.length > MAX_BODY_CHARS) {
            // Beyond the server ceiling the whole session would be excluded. Drop it silently.
            this._outbox.shift();
            this._persistOutbox();
            return;
        }

        const url = environment.api + '/establishments/' + encodeURIComponent(entry.e) + '/analytics';
        this._draining = true;
        this._post(url, text, unloading).then(
            (ok) => {
                this._draining = false;
                if (!ok) {
                    return;
                }
                this._outbox.shift();
                this._persistOutbox();
                if (this._outbox.length) {
                    this._drain(unloading);
                }
            },
            () => {
                this._draining = false;
            },
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Transport
    // -----------------------------------------------------------------------------------------------------

    /**
     * Posts one batch outside of `HttpClient`, on purpose.
     *
     * <p>`text/plain;charset=UTF-8` keeps the request CORS-simple, so no preflight can be left
     * hanging at the exact moment the tab goes to the background. `credentials: 'omit'` guarantees
     * the year-long fingerprint cookie never travels with it.
     *
     * <p>On the unload path the beacon is tried first: it is the only mechanism browsers commit to
     * completing, and `keepalive` on `fetch` has a patchy history on iOS Safari, which is the
     * dominant browser on a QR menu.
     */
    private _post(url: string, body: string, unloading: boolean): Promise<boolean> {
        if (unloading && this._beacon(url, body)) {
            return Promise.resolve(true);
        }
        try {
            if (typeof fetch === 'function') {
                return fetch(url, {
                    method: 'POST',
                    mode: 'cors',
                    credentials: 'omit',
                    cache: 'no-store',
                    keepalive: true,
                    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
                    body,
                })
                    .then((response) => !!response && (response.ok || response.status === 204))
                    .catch(() => this._beacon(url, body));
            }
        } catch {
            // Fall through to the beacon.
        }
        return Promise.resolve(this._beacon(url, body));
    }

    /**
     * Fallback transport. Returns whether the browser accepted the payload for delivery, which is
     * the only confirmation a beacon can give.
     */
    private _beacon(url: string, body: string): boolean {
        try {
            if (navigator && typeof navigator.sendBeacon === 'function') {
                return navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
            }
        } catch {
            // Some browsers throw on a disallowed content type. Nothing to report.
        }
        return false;
    }
}
